import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast, confirm } from '@rickylandino/react-messages';
import { Table, Empty, Modal, Space, Card } from 'antd';
import { DragOutlined } from '@ant-design/icons';
import { combineLatest } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { activeEventTypes, eventTypes } from '../../../services/OrganizationsService';
import SkeletonTable from '../../../helpers/SkeletonTable';
import update from 'immutability-helper';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { userInfo } from '../../../services/UsersService';
import { archiveEventType, getArchivedEventTypesByOrganization, insertEventType, updateEventType, updateEventTypes } from '../../../services/EventTypesService';
import { useMediaQuery } from 'react-responsive';
import Globals from '../../../config/globals';
import { useSetRecoilState } from 'recoil';
const type = 'DraggableBodyRow';

const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }) => {
    const ref = useRef(null);
    const [{ isOver, dropClassName }, drop] = useDrop({
        accept: type,
        collect: (monitor) => {
            const { index: dragIndex } = monitor.getItem() || {};
            if (dragIndex === index) {
                return {};
            }
            return {
                isOver: monitor.isOver(),
                dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
            };
        },
        drop: (item) => {
            moveRow(item.index, index);
        },
    });
    const [, drag] = useDrag({
        type,
        item: {
            index,
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });
    drop(drag(ref));
    return (
        <tr
            ref={ref}
            className={`${className}${isOver ? dropClassName : ''}`}
            style={{
                cursor: 'move',
                ...style,
            }}
            {...restProps}
        />
    );
};

export function ManageEventTypes(props) {
    const { register, getValues, setValue } = useForm();

    const [uInfo, setUInfo] = useState({});

    const [types, setTypes] = useState({
        allEventTypes: [],
        loaded: false
    });

    const [data, setData] = useState([]);

    const [selectedEventType, setSelectedEventType] = useState(null);

    const [archivedEventTypes, setArchivedEventTypes] = useState([]);
    const [selectedArchivedEventType, setSelectedArchivedEventType] = useState(null);

    const setActiveEventTypes = useSetRecoilState(activeEventTypes);

    var isDirty = useRef(false);

    const isDesktop = useMediaQuery({ minWidth: Globals.mobileWidth });

    useEffect(() => {
        var alive = true;

        combineLatest(
            eventTypes,
            userInfo
        ).pipe(takeWhile(() => alive)).subscribe(([data, userInfo]) => {
            setData([...data]);

            setTypes({
                ...types,
                allEventTypes: [...data],
                loaded: true
            });

            if (userInfo.users_ID) {
                setUInfo(userInfo);
                //TODO
                getArchivedEventTypesByOrganization(userInfo.organizations_ID).then(data => {
                    setArchivedEventTypes(data);
                });
            }
        });

        return () => { alive = false; }
    }, []);

    function handleOrderUpdate(eventTypesList) {
        let postdata = {
            eventTypesList,
            id: uInfo.organizations_ID
        }

        //TODO
        updateEventTypes(postdata).then(data => {
            if (data) {
                eventTypes.next(data);
                setActiveEventTypes(data);
                toast.success("Order has been updated");
            }
        });
    }

    function pushEventType(et) {
        let dt = [...data];

        setValue('formFields', et);
        dt.push(et);
        eventTypes.next(dt);
    }

    function handleSubmit() {
        let et = getValues().formFields;
        if (et.eventTypes_ID) {
            updateEventType(et).then(response => {
                if (response > 0) {
                    let dt = [...data];
                    let etIdx = dt.findIndex(t => t.eventTypes_ID === et.eventTypes_ID);
                    if (etIdx >= 0) {
                        data[etIdx] = et;

                        eventTypes.next(data);
                    }
                    setSelectedEventType(null)

                    toast.success("Event Type has been updated");
                } else {
                    toast.error("Something went wrong");
                }
            });
        } else {
            insertEventType(et).then(response => {
                if (response === 'duplicate') {
                    toast.error("This Event Type already exists");
                } else {
                    if (response > 0) {
                        et.eventTypes_ID = response;
                        pushEventType(et);

                        setSelectedEventType(et);
                        setSelectedEventType(null)
                        toast.success("Event Type has been added");
                    } else {
                        toast.error("Something went wrong");
                    }

                }
            })
        }
    }

    function addEventType() {
        let eventType = {
            eventType: '',
            organizations_ID: uInfo.organizations_ID,
            active: true,
            order: data.length + 1
        }

        setValue('formFields', eventType);

        setSelectedEventType(eventType);
    }

    function restoreEventType(index) {
        let aet = [...archivedEventTypes];

        let restoredObj = {
            ...archivedEventTypes[index],
            active: true,
            order: data.length + 1
        };

        //TODO
        updateEventType(restoredObj).then(response => {
            if (response >= 0) {
                pushEventType(restoredObj);

                aet.splice(index, 1);
                setArchivedEventTypes(aet);

                toast.success("Event Type has been restored");
            } else {
                toast.error("Something went wrong");
            }
        });

    }

    function removeEventType(index) {
        let dt = [...data];
        let aet = [...archivedEventTypes];
        confirm({
            title: "You are about to archive this event type.",
            content: "Are you sure you would like to proceed?",
            buttons: ["Yes", "No"],
            theme: window.sessionStorage.getItem("theme") === 'dark' ? 'dark' : 'light'
        },
            (ButtonPressed) => {
                if (ButtonPressed === "Yes") {
                    setSelectedEventType(null);

                    let etToRemove = dt[index];

                    //TODO
                    archiveEventType(etToRemove).then(response => {
                        if (response >= 0) {
                            aet.push(etToRemove);

                            dt.splice(index, 1);

                            dt.forEach((item, index) => {
                                item.order = index + 1;
                            });

                            setArchivedEventTypes(aet);
                            setData(dt);

                            eventTypes.next(dt);
                            toast.success("Event Type has been archived");
                        } else {
                            toast.error("Something went wrong");
                        }
                    });

                    return 0;
                }
                if (ButtonPressed === "No") {
                    return 0;
                }
            }
        );
    }

    const etColumns = [
        {
            title: 'Reorder',
            key: 'reorder',
            render: (_, record) => (
                <DragOutlined />
            ),
            width: 50,
            className: 'text-center move-cursor',
            hide: !isDesktop
        },
        {
            title: 'Event Type',
            dataIndex: 'eventType',
            key: 'eventType',
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <span id="edit" className="a hover">Edit</span>
                    <span id="delete" className="a hover">Archive</span>
                </Space>
            ),
            width: 200,
            className: 'text-center',
        }
    ];

    const archiveColumns = [
        {
            title: 'Archived Event Type',
            dataIndex: 'eventType',
            key: 'eventType',
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <span id="restore" className="a hover">Restore</span>
                </Space>
            ),
            width: 200,
            className: 'text-center',
        }
    ];

    const components = {
        body: {
            row: DraggableBodyRow,
        },
    };
    const moveRow = useCallback(
        (dragIndex, hoverIndex) => {
            const dragRow = data[dragIndex];

            let tempList = update(data, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, dragRow],
                ],
            });

            tempList.forEach((item, idx) => {
                item.order = idx + 1;
            });

            setData(tempList);
            handleOrderUpdate(tempList);

        },
        [data],
    );

    const columns = etColumns.filter(c => !c.hide);

    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <button className="ant-btn ant-btn-primary float-end" onClick={addEventType}>
                    <i className="far fa-plus-square"></i>
                    &nbsp;
                    Add Event Type
                </button>
            </div>

            <div className="row">
                <div className="col-lg-6 col-12 pb-4">
                    {types.loaded ?
                        <>
                            {data.length > 0 ?
                                <DndProvider backend={HTML5Backend}>
                                    <Table
                                        width={'50vw'}
                                        rowKey='eventTypes_ID'
                                        dataSource={data}
                                        columns={columns}
                                        size='small'
                                        pagination={false}
                                        rowClassName={(record, index) => record.eventTypes_ID === selectedEventType?.eventTypes_ID ? 'row-selected' : ''}
                                        components={components}
                                        onRow={(record, index) => {
                                            const attr = {
                                                index,
                                                moveRow,
                                            };

                                            return {
                                                onClick: event => {
                                                    if (event.target.id === 'delete') {
                                                        removeEventType(index);
                                                    } else {
                                                        setSelectedEventType(record); setValue('formFields', record);
                                                    }

                                                },
                                                index,
                                                moveRow
                                            };
                                        }}
                                    />
                                </DndProvider>
                                :
                                <Empty description={<p>Your organization currently has no event types - to begin adding an event type, click the "<i className="far fa-plus-square" /> Add Event Type" button above.</p>} />
                            }
                        </>
                        :
                        <SkeletonTable rowCount={5} columns={columns} />
                    }
                </div>

                <div className="col-lg-6 col-12">
                    {archivedEventTypes?.length > 0 &&
                        <Table
                            width={'50vw'}
                            rowKey='eventTypes_ID'
                            dataSource={archivedEventTypes}
                            columns={archiveColumns}
                            size='small'
                            pagination={false}
                            rowClassName={(record, index) => record.eventTypes_ID === selectedArchivedEventType?.eventTypes_ID ? 'row-selected' : ''}
                            onRow={(record, index) => {
                                return {
                                    onClick: event => {
                                        if (event.target.id === 'restore') {
                                            restoreEventType(index);
                                        }
                                    }
                                };
                            }}
                        />
                    }
                </div>

                <Modal
                    open={selectedEventType}
                    title={selectedEventType?.eventTypes_ID ? "Edit Event Type" : "Add Event Type"}
                    footer={[
                        <div className="text-start" key="1">
                            <button className="ant-btn ant-btn-primary text-start" onClick={handleSubmit}>Save</button>
                            <button className="ant-btn ant-btn-outline m-1" onClick={() => setSelectedEventType(null)}>Cancel</button>
                        </div>
                    ]}
                >
                    <div className="form-group col-lg-6 col-12">
                        <label>Event Type</label>
                        <input maxLength="25" className="ant-input" {...register('formFields.eventType')} />
                    </div>
                </Modal>
            </div>


        </>
    );
}
